import React, { FC, useEffect, useRef, useState } from "react";
import Modal from "react-modal";

import styles from "./AppStoreScreensModal.module.scss";

import { RightClickModal } from "@/shared/ui/RightClickModal/RightClickModal";
import { useSelectedAppStoreScreens } from "@/entities/selectedScreens";

interface AppStoreScreensModalProps {
  appStoreScreens;
  appStoreImageId?: string;
  appId?: string | null;
  isPro: boolean;
  refetchDataStore: () => void;
}

const AppStoreScreensModal: FC<AppStoreScreensModalProps> = ({
  appStoreScreens,
  appStoreImageId,
  appId,
  isPro,
  refetchDataStore,
}) => {
  const [zoom, setZoom] = useState(1);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageIndex, setImageIndex] = useState<number | null>(null);
  const [, setTotalImagesCount] = useState(0);
  const photoAppStoreScreens = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<{ [key: number]: HTMLImageElement | null }>({});

  const [isHover, setIsHover] = useState(false);

  // Add selected screens functionality
  const {
    addScreen,
    removeScreen,
    isSelected: checkIsSelected,
    selectedScreens,
  } = useSelectedAppStoreScreens();
  const [modalImageSelected, setModalImageSelected] = useState(false);

  useEffect(() => {
    if (appStoreScreens) {
      setTotalImagesCount(!isPro ? 2 : appStoreScreens.length);
    }
  }, [appStoreScreens]);

  const scrollToImage = (imageId: string) => {
    if (photoAppStoreScreens.current && imageRefs.current[imageId]) {
      const elementRect = imageRefs.current[imageId]!.getBoundingClientRect();
      const parentRect = photoAppStoreScreens.current!.getBoundingClientRect();

      const scrollToMiddle =
        elementRect.left -
        parentRect.left -
        parentRect.width / 2 +
        elementRect.width / 2;

      photoAppStoreScreens.current.scrollTo({
        left: photoAppStoreScreens.current.scrollLeft + scrollToMiddle,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (appStoreImageId) {
      scrollToImage(appStoreImageId);
      localStorage.removeItem(`appStoreScreens_${appId}`);
    }
  }, [appStoreImageId]);

  const applyZoom = (zoom: number) => {
    const photoCardListElement = photoAppStoreScreens.current;
    if (photoCardListElement) {
      photoCardListElement.style.setProperty("zoom", zoom.toString());
      photoCardListElement.style.setProperty("-ms-zoom", zoom.toString());
    }
  };

  useEffect(() => {
    applyZoom(zoom);
  }, [zoom]);

  const openImageModal = (item, image: string, index: number) => {
    setSelectedItem(item);
    setSelectedImage(item.thumbnail);
    setImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setSelectedItem(null);
    setImageIndex(null);
  };

  const handleNextImage = () => {
    if (imageIndex !== null && imageIndex < appStoreScreens.length - 1) {
      const nextIndex = imageIndex + 1;
      const nextItem = appStoreScreens[nextIndex];
      if (nextItem) {
        setSelectedItem(nextItem);
        setSelectedImage(nextItem.thumbnail);
        setImageIndex(nextIndex);
      }
    }
  };

  const handlePrevImage = () => {
    if (imageIndex !== null && imageIndex > 0) {
      const prevIndex = imageIndex - 1;
      const prevItem = appStoreScreens[prevIndex];
      if (prevItem) {
        setSelectedItem(prevItem);
        setSelectedImage(prevItem.thumbnail);
        setImageIndex(prevIndex);
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedImage) {
        if (event.key === "ArrowRight") {
          handleNextImage();
        } else if (event.key === "ArrowLeft") {
          handlePrevImage();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage, imageIndex, appStoreScreens]);

  const isLargeScreen = window.innerWidth >= 1740;
  const isFirstImage = imageIndex === 0;
  const isLastImage = imageIndex === appStoreScreens.length - 1;

  // State to manage RightClickModal
  const [rightClickModal, setRightClickModal] = useState<{
    visible: boolean;
    x: number;
    y: number;
    imageUrl: string;
    imageId: number;
    timestamp: number;
  }>({ visible: false, x: 0, y: 0, imageUrl: "", imageId: 0, timestamp: 0 });

  const handleContextMenu = (e: React.MouseEvent, item: any) => {
    e.preventDefault();
    setRightClickModal({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      imageUrl: item.image,
      imageId: item.id,
      timestamp: new Date(item.created_at).getTime(),
    });
  };

  const closeRightClickModal = () => {
    setRightClickModal((prev) => ({ ...prev, visible: false }));
  };

  // Handle selection for the main image view
  const handleSelectImage = (e: React.MouseEvent, item: any) => {
    e.stopPropagation();

    if (checkIsSelected(item.id)) {
      removeScreen(item.id);
      // If the modal is open and showing this image, update modal selection state too
      if (selectedItem && selectedItem.id === item.id) {
        setModalImageSelected(false);
      }
    } else {
      addScreen({
        id: item.id,

        is_saved: item.is_saved,
        is_saved_pk: item.is_saved_pk,
      });
      // If the modal is open and showing this image, update modal selection state too
      if (selectedItem && selectedItem.id === item.id) {
        setModalImageSelected(true);
      }
    }
  };

  // Handle selection for the modal image
  const handleModalSelectImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!selectedItem) return;

    if (modalImageSelected) {
      removeScreen(selectedItem.id);
      setModalImageSelected(false);
    } else {
      addScreen({
        id: selectedItem.id,

        is_saved: selectedItem.is_saved,
        is_saved_pk: selectedItem.is_saved_pk,
      });
      setModalImageSelected(true);
    }
  };

  // Update modal selection state when selectedScreens changes
  useEffect(() => {
    if (selectedItem) {
      setModalImageSelected(checkIsSelected(selectedItem.id));
    }
  }, [selectedScreens, selectedItem, checkIsSelected]);

  return (
    <>
      <div className={styles.root}>
        <div className={styles.containerPhotos} ref={photoAppStoreScreens}>
          <>
            {(isPro ? appStoreScreens : appStoreScreens).map((item, index) => {
              // Check if this item is selected
              const isItemSelected = checkIsSelected(item.id);

              return (
                <div
                  onClick={() => openImageModal(item, item.image, index)}
                  onContextMenu={(e) => handleContextMenu(e, item)}
                  key={item.id}
                  className={styles.imageContainer}
                >
                  {/* Always render the container with proper CSS classes */}
                  <div
                    className={`${styles.selectButtonContainer} ${isItemSelected ? styles.active : ""}`}
                  >
                    <div className={styles.selectButtonContainerInner}>
                      {/* Show saved tag ONLY when hovering (not when selected) and the item is saved */}
                      {item.is_saved && (
                        <div className={styles.savedTag}>
                          <p>Saved</p>
                        </div>
                      )}

                      <button
                        className={`${styles.selectButton} ${isItemSelected ? styles.selected : ""}`}
                        onClick={(e) => handleSelectImage(e, item)}
                      >
                        {isItemSelected && (
                          <img
                            src="/img/blackIcon/Check.svg"
                            alt="selectImage"
                          />
                        )}
                      </button>
                    </div>
                  </div>

                  <img
                    src={item.thumbnail}
                    alt={`Screen ${item.id}`}
                    className={styles.image}
                    ref={(el) => (imageRefs.current[item.id] = el)}
                    style={{
                      maxHeight: isLargeScreen
                        ? "805px"
                        : window.innerWidth <= 750
                          ? "500px"
                          : "715px",
                      maxWidth: isLargeScreen
                        ? "377.17px"
                        : window.innerWidth <= 750
                          ? "234px"
                          : "335px",
                    }}
                    loading="lazy"
                  />
                </div>
              );
            })}
          </>
        </div>
      </div>

      {rightClickModal.visible && (
        <RightClickModal
          x={rightClickModal.x}
          y={rightClickModal.y}
          onClose={closeRightClickModal}
          imageUrl={rightClickModal.imageUrl}
          imageId={rightClickModal.imageId}
          timestamp={rightClickModal.timestamp}
          isStoreScreen={true}
          handleOpenInNewTab={() => {}}
          refetchData={refetchDataStore}
          imageType="store"
          onlyCopy
        />
      )}

      <Modal
        isOpen={!!selectedImage}
        onRequestClose={closeImageModal}
        contentLabel="Image Modal"
        className={styles.modal}
        overlayClassName={styles.overlay}
      >
        <div
          className={styles.modalContent}
          onContextMenu={(e) => handleContextMenu(e, selectedItem)}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          <div className={styles.containerImage}>
            <img
              className={styles.modalContentPhoto}
              src={selectedImage!}
              alt="Enlarged pic"
              loading="lazy"
            />
            {(isHover || modalImageSelected) && (
              <div className={styles.modalSelectButtonContainer}>
                <div className={styles.modalSelectButtonContainerInner}>
                  {/* Show saved tag ONLY when hovering (not when selected) and the item is saved */}
                  {isHover && selectedItem && selectedItem.is_saved && (
                    <div className={styles.savedTag}>
                      <p>Saved</p>
                    </div>
                  )}

                  <button
                    className={`${styles.selectButton} ${modalImageSelected ? styles.selected : ""}`}
                    onClick={handleModalSelectImage}
                  >
                    {modalImageSelected && (
                      <img src="/img/blackIcon/Check.svg" alt="selectImage" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Replace the img tags with buttons for better accessibility */}
          <button
            className={styles.arrowRight}
            onClick={handleNextImage}
            style={{
              visibility: isLastImage ? "hidden" : "visible",
              cursor: isLastImage ? "default" : "pointer",
              background: "none",
              border: "none",
            }}
          >
            <img
              src="/img/whiteIcon/ChevronRight.svg"
              alt="Next image"
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </button>
          <button
            className={styles.arrowLeft}
            onClick={handlePrevImage}
            style={{
              visibility: isFirstImage ? "hidden" : "visible",
              cursor: isFirstImage ? "default" : "pointer",
              background: "none",
              border: "none",
            }}
          >
            <img
              src="/img/whiteIcon/ChevronLeft.svg"
              alt="Previous image"
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </button>
        </div>
      </Modal>
    </>
  );
};

export default AppStoreScreensModal;
